import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { SafeImage } from "@/components/SafeImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CATEGORIES } from "@/data/content";
import { useConfig } from "@/context/ConfigContext";
import { waLink } from "@/config/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

export default function CategoryDetail() {
  const { slug } = useParams();
  const cfg = useConfig();
  const category = CATEGORIES.find((c) => c.slug === slug);

  usePageMeta(
    category ? `${category.name} | Chhannulal & Brothers (CLB)` : "Category | Chhannulal & Brothers (CLB)",
    category ? `${category.short} Brands: ${category.brands.join(", ") || "selected quality brands"}. Enquire at Chhannulal & Brothers.` : undefined
  );

  if (!category) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center bg-cream pt-20" data-testid="category-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl font-light text-darknavy">Category not found</h1>
          <Link to="/categories" className="mt-6 inline-flex items-center gap-2 bg-navy px-6 py-3 font-display text-sm font-semibold uppercase tracking-wider text-cream hover:bg-aqua transition-colors">
            View all categories <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </main>
    );
  }

  const related = CATEGORIES.filter((c) => c.slug !== slug).slice(0, 3);

  return (
    <main>
      <section className="relative overflow-hidden bg-darknavy pb-24 pt-36 text-cream lg:pb-32 lg:pt-44" data-testid="category-hero">
        <div className="absolute inset-0 opacity-25">
          <SafeImage src={category.image} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-darknavy/60" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-gold">
              <Link to="/categories" className="hover:text-cream transition-colors">Categories</Link>
              <span className="mx-3 text-cream/40">/</span>
              {category.name}
            </p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-light tracking-tight sm:text-6xl">{category.name}</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/75 sm:text-lg">{category.description}</p>
            {category.brands.length > 0 && (
              <div className="mt-8 flex flex-wrap gap-2" data-testid="category-brands">
                {category.brands.map((b) => (
                  <span key={b} className="border border-gold/40 px-4 py-2 font-display text-xs font-medium uppercase tracking-[0.2em] text-gold">{b}</span>
                ))}
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <Overline>Key applications</Overline>
                <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy">Where it's used</h2>
                <ul className="mt-8 space-y-4" data-testid="category-applications">
                  {category.applications.map((a) => (
                    <li key={a} className="flex items-center gap-4 border-b border-darknavy/10 pb-4">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center bg-navy"><Check className="h-4 w-4 text-cream" /></span>
                      <span className="text-base text-darknavy/80">{a}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap gap-4">
                  <a href={waLink(cfg, `Hello Chhannulal & Brothers, I would like to enquire about ${category.name}.`)} target="_blank" rel="noopener noreferrer" data-testid="category-whatsapp-cta" className="flex items-center gap-2 bg-[#25D366] px-6 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-white transition-transform duration-300 hover:scale-[1.03]">
                    <WhatsAppIcon className="h-4 w-4" /> WhatsApp Enquiry
                  </a>
                  <Link to="/contact" data-testid="category-quote-cta" className="flex items-center gap-2 border border-navy px-6 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-navy transition-colors duration-300 hover:bg-navy hover:text-cream">
                    Get a Quote
                  </Link>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="bg-white p-8 shadow-[0_20px_60px_rgba(11,31,59,0.08)] lg:p-10">
                  <Overline>Enquire now</Overline>
                  <h2 className="mt-3 font-display text-2xl font-medium text-darknavy">Tell us your requirement</h2>
                  <div className="mt-8">
                    <EnquiryForm defaultCategory={category.slug} compact />
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24" data-testid="related-categories">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <Overline>Keep exploring</Overline>
            <h2 className="mt-4 font-display text-3xl font-light tracking-tight text-darknavy">Related Categories</h2>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.08}>
                <Link to={`/categories/${c.slug}`} className="group relative block h-56 overflow-hidden bg-navy" data-testid={`related-${c.slug}`}>
                  <SafeImage src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(11,31,59,0.9),transparent)]" />
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-5">
                    <span className="font-display text-lg font-medium text-cream">{c.name}</span>
                    <ArrowUpRight className="h-5 w-5 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
